import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import SmartDateInput from '../SmartDateInput';
import { toast } from 'sonner';
import { humanizeError } from '../../utils/errorHandling';
import {
  CUSTOMER_IMAGE_ACCEPT,
  CUSTOMER_IMAGE_FORMATS_LINE,
  IMAGE_PREVIEW_UNSUPPORTED_HINT,
  validateCustomerImageFile,
  normalizeImageUploadErrorDetail,
} from '../../utils/imageUploadValidation';
import {
  ATTACHMENT_MODE_LABELS,
  MARKER_TYPE_LABELS,
  MARKER_TYPE_ORDER,
  MARKER_TYPE_ICONS,
  buildPersonAttachment,
  buildTimelineOnlyAttachment,
  createMemoryMarker,
  deleteMemoryMarkerImage,
  formatTagsForInput,
  getMarkerDisplayImage,
  markerEmptyPayload,
  parseTagsInput,
  updateMemoryMarker,
  uploadMemoryMarkerImage,
} from '../../utils/memoryMarkers';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getApiBase } from '../../utils/apiBase';

const BACKEND_URL = getApiBase().replace(/\/api$/, '');

function personLabel(p) {
  const gn = (p?.given_names || '').trim();
  const sn = (p?.surname || '').trim();
  return `${gn} ${sn}`.trim() || 'Person';
}

/**
 * Create or edit a connected memory (place, object, milestone, event).
 */
export function MemoryMarkerDialog({
  open,
  onOpenChange,
  treeId,
  marker = null,
  defaultPersonId = null,
  allowAttachmentPick = false,
  genealogicalPeople = [],
  stories = [],
  onSaved,
}) {
  const isEdit = Boolean(marker?.id);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => markerEmptyPayload({ personId: defaultPersonId }));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviewFailed, setImagePreviewFailed] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setImageFile(null);
    setRemoveImage(false);
    if (marker) {
      const attachment = marker.attachment || {};
      setForm({
        marker_type: marker.marker_type || 'place',
        title: marker.title || '',
        description: marker.description || '',
        approx_date: marker.approx_date || '',
        attachment: {
          mode: attachment.mode || 'person',
          person_id: attachment.person_id || defaultPersonId || null,
          person_id_2: attachment.person_id_2 || null,
          partnership_id: attachment.partnership_id || null,
        },
        linked_story_id: marker.linked_story_id || null,
        location_label: marker.location_label || '',
        tags: marker.tags || [],
      });
      const existing = getMarkerDisplayImage(marker);
      setImagePreview(existing ? resolveAssetUrl(BACKEND_URL, existing) : null);
    } else {
      setForm(markerEmptyPayload({ personId: defaultPersonId }));
      setImagePreview(null);
    }
  }, [open, marker, defaultPersonId]);

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const tagsInput = useMemo(() => formatTagsForInput(form.tags), [form.tags]);

  const attachmentMode = form.attachment?.mode || 'person';

  const handleAttachmentModeChange = (mode) => {
    setForm((prev) => ({
      ...prev,
      attachment:
        mode === 'timeline_only'
          ? buildTimelineOnlyAttachment()
          : buildPersonAttachment(prev.attachment?.person_id || defaultPersonId),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const title = (form.title || '').trim();
    if (!title) {
      toast.error('Please add a title for this memory.');
      return;
    }
    if (!treeId) return;

    const payload = {
      marker_type: form.marker_type,
      title,
      description: (form.description || '').trim() || null,
      approx_date: (form.approx_date || '').trim() || null,
      attachment: form.attachment,
      linked_story_id: form.linked_story_id || null,
      location_label: (form.location_label || '').trim() || null,
      tags: Array.isArray(form.tags) ? form.tags : parseTagsInput(form.tags),
    };

    setSaving(true);
    try {
      const saved = isEdit
        ? await updateMemoryMarker(marker.id, payload)
        : await createMemoryMarker(treeId, payload);
      const markerId = saved?.id || marker?.id;
      let imageWarning = null;
      if (markerId && imageFile) {
        try {
          await uploadMemoryMarkerImage(markerId, imageFile);
        } catch (imgErr) {
          imageWarning = normalizeImageUploadErrorDetail(humanizeError(imgErr));
        }
      } else if (markerId && removeImage && isEdit) {
        await deleteMemoryMarkerImage(markerId);
      }
      toast.success(
        isEdit ? 'Connected memory updated.' : 'This memory has been added to the archive.',
      );
      if (imageWarning) {
        toast.warning('Memory saved, but the photo could not be uploaded.', {
          description: imageWarning,
        });
      }
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[MyLegacyBridge] Connected memory save failed', {
        status: err?.response?.status,
        detail: err?.response?.data?.detail,
        url: err?.config?.url,
      });
      toast.error(humanizeError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const check = validateCustomerImageFile(file);
    if (!check.ok) {
      toast.error(check.message);
      return;
    }
    setImageFile(file);
    setRemoveImage(false);
    setImagePreviewFailed(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    setRemoveImage(Boolean(marker && getMarkerDisplayImage(marker)));
    setImagePreview(null);
    setImagePreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)] sm:max-w-lg"
        data-testid="memory-marker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-normal text-[#1c1917]">
            {isEdit ? 'Edit connected memory' : 'Add a connected memory'}
          </DialogTitle>
          <DialogDescription className="text-[0.95rem] leading-relaxed text-[#5c534a]">
            Add a place, object, milestone or event — a gentle anchor in your family story.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-1">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Type</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {MARKER_TYPE_ORDER.map((type) => {
                const Icon = MARKER_TYPE_ICONS[type] || MARKER_TYPE_ICONS.object;
                const selected = form.marker_type === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, marker_type: type }))}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-center text-xs transition ${
                      selected
                        ? 'border-[#8B7355]/50 bg-[#8B7355]/8 text-[#1c1917]'
                        : 'border-[hsl(38_14%_88%)] bg-background/60 text-muted-foreground hover:border-[#8B7355]/25'
                    }`}
                    data-testid={`marker-type-${type}`}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    {MARKER_TYPE_LABELS[type]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="marker-title">Title</Label>
            <Input
              id="marker-title"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder={
                form.marker_type === 'place'
                  ? 'e.g. Oak Street house'
                  : form.marker_type === 'object'
                    ? 'e.g. First Ford truck'
                    : 'e.g. Family reunion at the lake'
              }
              required
              className="border-[hsl(38_14%_88%)] bg-white"
              data-testid="marker-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="marker-description">Description (optional)</Label>
            <Textarea
              id="marker-description"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="A few words about why this matters…"
              rows={3}
              className="resize-none border-[hsl(38_14%_88%)] bg-white"
              data-testid="marker-description-input"
            />
          </div>

          <SmartDateInput
            mode="simple"
            label="Approximate year or date (optional)"
            value={form.approx_date}
            onChange={(val) => setForm((p) => ({ ...p, approx_date: val || '' }))}
            placeholder="e.g. 1988, ABT-1970"
            data-testid="marker-date-input"
          />
          <p className="-mt-2 text-[11px] text-muted-foreground">
            Dated memories can appear on your family timeline.
          </p>

          {form.marker_type === 'place' ? (
            <div className="space-y-2">
              <Label htmlFor="marker-location">Location label (optional)</Label>
              <Input
                id="marker-location"
                value={form.location_label}
                onChange={(e) => setForm((p) => ({ ...p, location_label: e.target.value }))}
                placeholder="e.g. Boston, MA"
                className="border-[hsl(38_14%_88%)] bg-white"
              />
            </div>
          ) : null}

          {allowAttachmentPick ? (
            <div className="space-y-3 rounded-xl border border-[hsl(38_14%_88%)]/80 bg-background/40 p-3">
              <Label className="text-sm font-medium">Attachment</Label>
              <Select value={attachmentMode} onValueChange={handleAttachmentModeChange}>
                <SelectTrigger className="border-[hsl(38_14%_88%)] bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">{ATTACHMENT_MODE_LABELS.person}</SelectItem>
                  <SelectItem value="timeline_only">{ATTACHMENT_MODE_LABELS.timeline_only}</SelectItem>
                </SelectContent>
              </Select>
              {attachmentMode === 'person' ? (
                <Select
                  value={form.attachment?.person_id || ''}
                  onValueChange={(id) =>
                    setForm((p) => ({ ...p, attachment: buildPersonAttachment(id) }))
                  }
                >
                  <SelectTrigger className="border-[hsl(38_14%_88%)] bg-white">
                    <SelectValue placeholder="Choose a person" />
                  </SelectTrigger>
                  <SelectContent>
                    {genealogicalPeople.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {personLabel(p)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          ) : null}

          {stories.length > 0 ? (
            <div className="space-y-2">
              <Label>Linked story (optional)</Label>
              <Select
                value={form.linked_story_id || '__none__'}
                onValueChange={(val) =>
                  setForm((p) => ({ ...p, linked_story_id: val === '__none__' ? null : val }))
                }
              >
                <SelectTrigger className="border-[hsl(38_14%_88%)] bg-white">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {stories.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {(s.display_title || s.question_text || 'Story').slice(0, 60)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label>Photo (optional)</Label>
            {imagePreview ? (
              <div className="relative inline-block">
                {imagePreviewFailed ? (
                  <p className="max-w-[14rem] text-xs text-muted-foreground">{IMAGE_PREVIEW_UNSUPPORTED_HINT}</p>
                ) : (
                  <img
                    src={imagePreview}
                    alt=""
                    className="h-28 w-28 rounded-xl border border-[hsl(38_14%_88%)] object-cover"
                    onError={() => setImagePreviewFailed(true)}
                  />
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute -right-2 -top-2 h-7 w-7 rounded-full shadow"
                  onClick={clearImage}
                  aria-label="Remove photo"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="rounded-full border-[hsl(38_14%_88%)]"
                onClick={() => fileInputRef.current?.click()}
                data-testid="marker-photo-upload-btn"
              >
                <ImagePlus className="mr-2 h-4 w-4" />
                Add a photo
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={CUSTOMER_IMAGE_ACCEPT}
              className="hidden"
              onChange={handleImagePick}
            />
            <p className="text-[11px] text-muted-foreground">
              {CUSTOMER_IMAGE_FORMATS_LINE} — we keep the original and optimize for display.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="marker-tags">Tags (optional)</Label>
            <Input
              id="marker-tags"
              value={tagsInput}
              onChange={(e) => setForm((p) => ({ ...p, tags: parseTagsInput(e.target.value) }))}
              placeholder="home, childhood, summer"
              className="border-[hsl(38_14%_88%)] bg-white"
            />
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !form.title.trim()}
              className="rounded-full bg-primary text-primary-foreground"
              data-testid="marker-save-btn"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add memory'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default MemoryMarkerDialog;
